import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { pageDestinationId } from '../lib/destinations.js';
import { DestinationFilter } from './DestinationPicker.js';
import { withChannel } from '../lib/channels.js';
import { useFormatters, useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { Icon } from './Icons.js';

/**
 * The pictures a published post carried, wherever they came from.
 *
 * Files of ours are served back through the API; a republished post's are the
 * addresses Meta gave us for the original. Both are just `src` here.
 */
function pictures(post: { mediaIds: string[]; imageUrls: string[] }): string[] {
  return [...post.mediaIds.map((id) => `/api/media/${id}`), ...post.imageUrls];
}

/**
 * What Meta says about one post, asked at the moment somebody wonders.
 *
 * This exists for a specific complaint: a post goes out, and some people see
 * it on Facebook while others — including people who manage the Page — do not.
 * Every fact that decides that lives on the post, and the dashboard used to
 * have no way to ask. Now it asks, and reports the answer in the operator's
 * language rather than as Graph fields.
 *
 * The empty result is deliberately given a sentence of its own. "Meta reports
 * nothing restricting this post" is a real finding: it moves the question off
 * settings and onto Feed distribution, where not everybody is shown every
 * post and nothing is wrong.
 */
function PostDetails({ postId, pageId }: { postId: string; pageId: string }) {
  const { t } = useI18n();
  const fmt = useFormatters();

  const details = useQuery({
    queryKey: ['post-details', postId],
    queryFn: () => api.postDetails(postId, pageId),
    // A live read of one post. Asked when opened, not on a timer: the answer
    // changes when a person changes something, not by itself.
    refetchOnWindowFocus: false,
  });

  if (details.isLoading) return <div className="loading">{t('common.loading')}</div>;

  if (details.isError) {
    return (
      <div className="notice error">
        {details.error instanceof ApiError ? details.error.message : t('published.detailsFailed')}
      </div>
    );
  }

  const post = details.data?.details;
  if (!post) return null;

  return (
    <div className="post-details">
      <dl className="detail-grid">
        <div>
          <dt>{t('published.onFacebook')}</dt>
          <dd>{post.createdTime ? fmt.dateTime(post.createdTime) : '—'}</dd>
        </div>
        <div>
          <dt>{t('published.audience')}</dt>
          <dd>{post.privacy?.description ?? post.privacy?.value ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('published.publishedBy')}</dt>
          {/* Which app made it. A post published from the Facebook app or
              Business Suite says so here, which settles "did this even come
              from us?" without anybody guessing. */}
          <dd>{post.application?.name ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('published.stillOnPage')}</dt>
          <dd>
            {post.isPublished === false
              ? t('common.no')
              : post.isHidden === true
                ? t('published.hiddenFromTimeline')
                : t('common.yes')}
          </dd>
        </div>
      </dl>

      {post.findings.length === 0 ? (
        <p className="tiny muted">{t('published.nothingRestricting')}</p>
      ) : (
        <ul className="findings">
          {post.findings.map((finding) => (
            <li key={finding.code}>
              <strong>{t(`published.finding.${finding.code}` as TranslationKey)}</strong>
              {finding.detail && <span className="tiny muted"> · {finding.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Posts this system published, with the ability to delete one.
 *
 * The gap this fills: publishing to the wrong Page, or publishing a draft by
 * mistake, previously meant going to Facebook to clean up. Meta allows an app
 * to delete a post **it created**, which covers exactly this case.
 */
export function PublishedPostsPanel() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const fmt = useFormatters();
  const [notice, setNotice] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);
  /** Which row has its details open. One at a time: each one is a Graph call. */
  const [open, setOpen] = useState<string | null>(null);
  /** A destination id (`page:…` / `soc:…`), or '' for every one. */
  const [destination, setDestination] = useState('');

  const posts = useQuery({
    queryKey: ['published-posts'],
    queryFn: api.publishedPosts,
  });

  const remove = useMutation({
    mutationFn: ({ postId, pageId }: { postId: string; pageId: string }) =>
      api.deletePost(postId, pageId),
    onSuccess: (data) => {
      setNotice({ kind: 'ok', text: data.notice });
      void queryClient.invalidateQueries({ queryKey: ['published-posts'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('published.deleteFailed'),
      }),
  });

  /*
   * Meta gives no way to choose a reaction here — only a plain Like, placed or
   * removed. There is also no way to ask whether the Page already liked this
   * post, so the two buttons stay independent rather than one toggle
   * pretending to know a state it cannot see.
   */
  const like = useMutation({
    mutationFn: ({ postId, pageId }: { postId: string; pageId: string }) =>
      api.likePost(postId, pageId),
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('published.likeFailed'),
      }),
  });
  const unlike = useMutation({
    mutationFn: ({ postId, pageId }: { postId: string; pageId: string }) =>
      api.unlikePost(postId, pageId),
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('published.unlikeFailed'),
      }),
  });

  const removeSocial = useMutation({
    mutationFn: (args: { accountId: string; postId: string }) =>
      api.deleteSocialPost(args.accountId, args.postId),
    onSuccess: () => {
      setNotice({ kind: 'ok', text: t('source.deleted') });
      void queryClient.invalidateQueries({ queryKey: ['published-posts'] });
    },
    onError: (error) =>
      setNotice({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('published.deleteFailed'),
      }),
  });

  if (posts.isLoading) return <div className="loading">{t('common.loading')}</div>;

  const rows = (posts.data?.posts ?? []).filter(
    (post) =>
      !destination ||
      destination === (post.channel === 'FACEBOOK' ? pageDestinationId(post.pageId) : `soc:${post.pageId}`),
  );

  return (
    <section className="panel">
      <h2>{t('published.title')}</h2>
      <p className="muted small">{t('published.subtitle')}</p>

      <div className="inline-form">
        <DestinationFilter
          feature={DestinationFeature.HISTORY}
          value={destination}
          allLabel={t('inbox.allPages')}
          onChange={setDestination}
        />
      </div>

      {notice && (
        <div className={notice.kind === 'ok' ? 'notice ok' : 'notice error'}>{notice.text}</div>
      )}

      {rows.length === 0 ? (
        <div className="empty-state">
          <Icon.check />
          <p>{t('published.empty')}</p>
        </div>
      ) : (
        <div className="grid-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>{t('common.page')}</th>
                <th>{t('published.post')}</th>
                <th>{t('published.publishedAt')}</th>
                <th>{t('common.link')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((post) => (
                <Fragment key={post.postId}>
                  <tr>
                    <td>
                      <strong>{withChannel(t, post.channel, post.pageName)}</strong>
                    </td>
                    {/* What it says, then what it is called. The id is still
                      here — it is what the delete call names, and what a
                      person compares against Facebook when something looks
                      wrong — but it is no longer the only thing to go on. */}
                    <td className="text-cell">
                      {post.message ? (
                        <p className="published-text">{post.message}</p>
                      ) : (
                        <p className="tiny muted">{t('common.noText')}</p>
                      )}

                      {pictures(post).length > 0 && (
                        <div className="job-preview-media">
                          {pictures(post).map((picture) => (
                            /* Decorative: the text beside it already says what
                             the post is, and a Graph id read aloud says
                             nothing at all. */
                            <img key={picture} src={picture} alt="" loading="lazy" />
                          ))}
                        </div>
                      )}

                      <span className="mono tiny muted published-id" title={post.postId}>
                        {post.postId}
                      </span>
                    </td>
                    <td className="small">{fmt.dateTime(post.publishedAt)}</td>
                    <td>
                      <a href={post.permalink} target="_blank" rel="noreferrer noopener">
                        {t('common.open')} <Icon.external />
                      </a>
                    </td>
                    <td>
                      {post.channel !== 'FACEBOOK' && (
                        <button
                          type="button"
                          className="danger small"
                          disabled={removeSocial.isPending}
                          onClick={() => {
                            if (window.confirm(t('source.confirmDeleteSocial'))) {
                              removeSocial.mutate({ accountId: post.pageId, postId: post.postId });
                            }
                          }}
                        >
                          <Icon.trash />
                          {t('common.delete')}
                        </button>
                      )}
                      {/* Inspecting and liking are Facebook's; a social post is deleted above. */}
                      {post.channel === 'FACEBOOK' && (
                        <>
                      <button
                        type="button"
                        className="ghost small"
                        onClick={() => setOpen(open === post.postId ? null : post.postId)}
                      >
                        {open === post.postId ? t('common.close') : t('published.why')}
                      </button>
                      <button
                        type="button"
                        className="ghost small"
                        disabled={like.isPending}
                        title={t('published.likeHint')}
                        onClick={() => like.mutate({ postId: post.postId, pageId: post.pageId })}
                      >
                        👍 {t('published.like')}
                      </button>
                      <button
                        type="button"
                        className="ghost small"
                        disabled={unlike.isPending}
                        onClick={() => unlike.mutate({ postId: post.postId, pageId: post.pageId })}
                      >
                        {t('published.unlike')}
                      </button>
                      <button
                        type="button"
                        className="danger small"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              t('published.confirmDelete', {
                                page: post.pageName,
                                id: post.postId,
                              }),
                            )
                          ) {
                            remove.mutate({
                              postId: post.postId,
                              pageId: post.pageId,
                            });
                          }
                        }}
                      >
                        <Icon.trash />
                        {t('common.delete')}
                      </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {/* Under the row it belongs to rather than in a dialog: it is
                    an answer about that post and reads next to it. */}
                  {open === post.postId && post.channel === 'FACEBOOK' && (
                    <tr className="detail-row">
                      <td colSpan={5}>
                        <PostDetails postId={post.postId} pageId={post.pageId} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
